'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, user, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);

  const callbackError = searchParams?.get('error');

  useEffect(() => {
    if (!loading && user) {
      router.replace('/app');
    }
  }, [user, loading, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signIn(email, password);
      router.replace('/app');
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github') => {
    setOauthLoading(provider);
    setError('');
    try {
      const supabase = createClient();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback?next=/app` },
      });
      if (oauthError) throw oauthError;
    } catch (err: any) {
      setError(err?.message || 'OAuth sign-in failed. Please try again.');
      setOauthLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 bg-warm border border-ink/10 rounded-2xl text-sm text-ink placeholder-ink/30 focus:outline-none focus:border-teal/50 focus:ring-2 focus:ring-teal/10 transition-all";

  return (
    <div className="min-h-screen bg-warm flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-30 -translate-y-1/2 translate-x-1/2" style={{ background: '#E8FAF9' }} />
      <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full opacity-20 translate-y-1/2 -translate-x-1/2" style={{ background: '#F4EDF9' }} />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link href="/home">
            <AppLogo size={32} text="QOLLECTIONS" className="cursor-pointer" />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-soft-md overflow-hidden">
          {/* Card header */}
          <div className="px-6 py-5 border-b border-ink/8" style={{ background: 'linear-gradient(135deg, #E8FAF9, #E6F9EF)' }}>
            <h1 className="text-xl font-bold text-ink text-center">Welcome back 👋</h1>
            <p className="text-sm text-ink/50 text-center mt-1">Sign in to your collection</p>
          </div>

          <div className="p-6">
            {/* Errors */}
            {(callbackError || error) && (
              <div className="mb-5 bg-rose-pastel border border-rose/20 rounded-2xl px-4 py-3">
                <p className="text-xs font-semibold text-rose">⚠ {error || 'Authentication error. Please try again.'}</p>
              </div>
            )}

            {/* OAuth buttons */}
            <div className="space-y-3 mb-5">
              <button
                onClick={() => handleOAuth('google')}
                disabled={!!oauthLoading || submitting}
                className="w-full flex items-center justify-center gap-3 bg-white border border-ink/10 hover:border-ink/25 rounded-2xl px-4 py-3 text-sm font-medium text-ink transition-all hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading === 'google' ? (
                  <div className="w-4 h-4 border-2 border-ink/20 border-t-ink rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>Continue with Google</span>
              </button>

              <button
                onClick={() => handleOAuth('github')}
                disabled={!!oauthLoading || submitting}
                className="w-full flex items-center justify-center gap-3 bg-ink hover:bg-ink/90 rounded-2xl px-4 py-3 text-sm font-medium text-white transition-all hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading === 'github' ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
                  </svg>
                )}
                <span>Continue with GitHub</span>
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-ink/8" />
              <span className="text-xs font-medium text-ink/30 tracking-widest">OR</span>
              <div className="flex-1 h-px bg-ink/8" />
            </div>

            {/* Email/password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink/40 mb-1.5">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="collector@qollections.app" className={inputClass} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink/40">Password</label>
                  <Link href="/auth/forgot-password" className="text-xs font-medium text-teal hover:opacity-70 transition-opacity">Forgot?</Link>
                </div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className={inputClass} />
              </div>

              <button
                type="submit"
                disabled={submitting || !!oauthLoading}
                className="w-full py-3 rounded-2xl text-sm font-semibold text-white flex items-center justify-center gap-2 mt-1 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                style={{ background: 'linear-gradient(135deg, #4ECDC4, #2ECC71)' }}
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /><span>Signing In…</span></>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-ink/50">
              New to Qollections?{' '}
              <Link href="/auth/signup" className="font-semibold text-teal hover:opacity-70 transition-opacity">Create an account</Link>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink/25 tracking-wider">Your collection. Your community.</p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-warm flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
